import { useCallback, useEffect, useRef, useState } from "react";
import { acceptInvite, checkInvite, enroll2fa, type InvitePreview } from "../api/auth";
import Setup2FAPage from "./Setup2FAPage";

/**
 * Read the one-time token out of the URL fragment.
 *
 * The daemon mints `/invite#<token>`, not `?token=<token>`, because a fragment
 * is never put on the wire: it cannot reach an access log, a proxy, or the
 * Referer header of anything the page later loads. Reading it here is the only
 * place the token is ever handled, and the page scrubs it from the address bar
 * immediately afterwards.
 */
export function tokenFromHash(hash: string): string {
  return hash.startsWith("#") ? hash.slice(1).trim() : hash.trim();
}

/**
 * What the page is currently showing.
 *
 * Three ways this can fail, and they are NOT interchangeable:
 *  - `no_token`: nothing in the fragment. Usually a reload or a bookmark,
 *    because the token is scrubbed on mount. The link itself is fine, so the
 *    copy must send the user back to their message, not tell them it is dead.
 *  - `rejected`: the daemon refused the token. Final, so that screen offers
 *    nothing to press — retrying is useless and is the one thing that would
 *    turn this page into a token oracle.
 *  - `unreachable`: the daemon did not answer. The token is still good and
 *    still in memory, so this one gets a retry that re-runs the check.
 *    It must never advise a reload: that would discard the token and present
 *    a working link as a dead one.
 */
type Phase =
  | { kind: "checking" }
  | { kind: "no_token" }
  | { kind: "rejected" }
  | { kind: "unreachable"; message: string }
  | { kind: "form"; invite: InvitePreview }
  | { kind: "setup2fa"; enrollToken: string; provisioningUri: string; secret: string }
  | { kind: "enroll_blocked"; reason: "expired" | "already_enrolled" }
  | { kind: "done"; email: string };

export default function InvitePage() {
  // Captured on the first render, before the effect below scrubs it. From here
  // on the credential lives in memory only.
  const [token] = useState(() =>
    typeof window === "undefined" ? "" : tokenFromHash(window.location.hash),
  );

  const [phase, setPhase] = useState<Phase>({ kind: "checking" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drop the token from the address bar the moment it has been read. Left
  // there it would ride along into history sync, session restore, a screenshot
  // or a shoulder-surfer, and it stays live until it is redeemed or expires.
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {
      /* about:blank and similar documents forbid history writes */
    }
  }, []);

  // Only the newest check may write a phase, so a retry cannot be overtaken by
  // the slower response it was retrying.
  const attempt = useRef(0);

  // Validate the link before asking for a password, so a dead link costs the
  // invitee one screen instead of a filled-in form and a rejection. Re-runnable
  // by the retry button: the token is in state, so this needs no reload and no
  // storage — which matters, because a reload is exactly what loses it.
  const check = useCallback(() => {
    if (!token) {
      setPhase({ kind: "no_token" });
      return;
    }
    const mine = ++attempt.current;
    setPhase({ kind: "checking" });
    checkInvite(token)
      .then((invite) => {
        if (mine === attempt.current) setPhase(invite ? { kind: "form", invite } : { kind: "rejected" });
      })
      .catch((e) => {
        if (mine === attempt.current) setPhase({ kind: "unreachable", message: (e as Error).message });
      });
  }, [token]);

  useEffect(() => { check(); }, [check]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Mirror the daemon's own rule so the common mistakes never cost a request.
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      const r = await acceptInvite(token, password);
      if (r.kind === "invalid") setPhase({ kind: "rejected" });
      else if (r.kind === "needs_setup") {
        setPhase({
          kind: "setup2fa",
          enrollToken: r.enrollToken,
          provisioningUri: r.totpProvisioningUri,
          secret: r.totpSecret,
        });
      } else setPhase({ kind: "done", email: r.email });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (phase.kind === "setup2fa") {
    // The same enrolment screen the first-login path uses, with only the
    // submit swapped: an invite enrols against its short-lived enrol token,
    // not against /auth/2fa/setup, which is unauthenticated and keyed on an
    // enumerable userId. That call mints the session, so the user is signed in
    // once it succeeds — hence a navigation to the app root rather than
    // LoginPage's reload, which would land back on this page with nothing.
    return (
      <Setup2FAPage
        provisioningUri={phase.provisioningUri}
        secret={phase.secret}
        verify={async (code) => {
          const r = await enroll2fa(phase.enrollToken, code);
          // The throw is load-bearing, not decoration: Setup2FAPage treats a
          // resolved verify as success and calls onDone, which would navigate
          // this user into the app with no session. Throwing is the contract's
          // "did not succeed", and it is what skips onDone. The message is
          // never seen — the phase change has already replaced this screen.
          if (r.kind !== "ok") {
            setPhase({ kind: "enroll_blocked", reason: r.kind });
            throw new Error(`enrolment blocked: ${r.kind}`);
          }
        }}
        onDone={() => { window.location.href = "/"; }}
      />
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1>ThunderAy AI</h1>

        {phase.kind === "checking" && <p>Checking your invite…</p>}

        {phase.kind === "no_token" && (
          <>
            <h2>Open your invite link again</h2>
            <p>
              This page takes a one-time code from the link and clears it from
              the address bar straight away, so a reload or a bookmark loses
              it. Open the original link from your message — it still works.
            </p>
          </>
        )}

        {phase.kind === "rejected" && (
          <>
            <h2>This link no longer works</h2>
            <p>
              Sign-in links can be used once, and they expire. Ask an admin to
              send you a new one.
            </p>
          </>
        )}

        {phase.kind === "unreachable" && (
          <>
            <h2>Could not check your invite</h2>
            <p>The server did not answer. Your link is still good — try again.</p>
            <p className="auth-error">{phase.message}</p>
            <button type="button" onClick={check}>
              Try again
            </button>
          </>
        )}

        {phase.kind === "enroll_blocked" && (
          <>
            <h2>
              {phase.reason === "expired"
                ? "Two-factor setup timed out"
                : "This account is already set up"}
            </h2>
            <p>
              Your password was saved.{" "}
              {phase.reason === "expired"
                ? "Sign in with it to finish setting up your authenticator app."
                : "Sign in with it and the code from your existing authenticator app."}
            </p>
            <button type="button" onClick={() => { window.location.href = "/"; }}>
              Go to sign in
            </button>
          </>
        )}

        {phase.kind === "done" && (
          <>
            <h2>Your password is set</h2>
            <p>
              Sign in as <strong>{phase.email}</strong> with
              your new password and the code from your authenticator app.
            </p>
            <button type="button" onClick={() => { window.location.href = "/"; }}>
              Go to sign in
            </button>
          </>
        )}

        {phase.kind === "form" && (
          <>
            <p>
              Choose a password for <strong>{phase.invite.email}</strong>.
              {phase.invite.needsTotp
                ? " You will set up two-factor auth next."
                : " Your existing authenticator app still applies."}
            </p>
            <form onSubmit={submit}>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  autoComplete="new-password"
                />
              </label>
              <label>
                Confirm password
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </label>
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" disabled={busy || !password || !confirm}>
                {busy ? "Saving…" : "Set password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
